import {
  AfterViewInit,
  Component,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { ActivatedRoute, Router } from '@angular/router';
import { tap } from 'rxjs/operators';
import { AppService } from 'src/app/app.service';
import { ApiRequestService } from 'src/app/shared/api-request.service';
import { UtilsService } from 'src/app/shared/utils.service';
import { CustomDataSource } from 'src/app/utils/custom-data-source';
import { FolderModel } from 'src/app/shared/folder.model';
import { SiteModel } from 'src/app/models/site.model';
import { FolderService } from 'src/app/shared/folder.service';
import { MatDialog } from '@angular/material/dialog';
import { SitesDocsFilterComponent } from '../sites-docs-filter/sites-docs-filter.component';
import { AccountDocsLayoutComponent } from '../../account/account-docs/account-docs-layout/account-docs-layout.component';
import { TemplateDocsViewComponent } from '../../template-docs/template-docs-view/template-docs-view.component';
import { RBACService } from 'src/app/shared/rbac.service';

@Component({
  selector: 'app-sites-docs',
  templateUrl: './sites-docs.component.html',
  styleUrls: ['./sites-docs.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class SitesDocsComponent implements OnInit, AfterViewInit {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  dataSource = new SitesLibraryDataSource(this.app, this.api);

  displayedColumns: string[] = [];

  query: string;
  isEdit = window.location.toString().match(/edit/);

  parent_id: number;
  child_id: number;
  site_id: number;

  // RBAC permission flags. Resolved once in ngOnInit() so the template does not
  // re-evaluate permission lookups on every change detection cycle.
  canView: boolean = false;
  canCreate: boolean = false;
  canEdit: boolean = false;
  canDelete: boolean = false;

  constructor(
    private folderService: FolderService,
    public utils: UtilsService,
    private app: AppService,
    private api: ApiRequestService,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    public rbac: RBACService,
    private router: Router
  ) {}

  ngOnInit() {
    // Resolve the RBAC permission flags first so they are always set, even when
    // one of the access guards below short circuits the rest of this method.
    this.canView = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES,
          RBACService.RBAC_A.A_VIEW
        ),
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES_DOCUMENTS,
          RBACService.RBAC_A.A_VIEW
        )
      ],
      true
    );
    this.canCreate = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES,
          RBACService.RBAC_A.A_VIEW
        ),
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES_DOCUMENTS,
          RBACService.RBAC_A.A_CREATE
        )
      ],
      true
    );
    this.canEdit = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES,
          RBACService.RBAC_A.A_VIEW
        ),
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES_DOCUMENTS,
          RBACService.RBAC_A.A_VIEW
        ),
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES_DOCUMENTS,
          RBACService.RBAC_A.A_EDIT
        )
      ],
      true
    );
    this.canDelete = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES,
          RBACService.RBAC_A.A_VIEW
        ),
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES_DOCUMENTS,
          RBACService.RBAC_A.A_VIEW
        ),
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES_DOCUMENTS,
          RBACService.RBAC_A.A_DELETE
        )
      ],
      true
    );

    // Check if user has sites:view permission first
    const hasSitesView = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES,
          RBACService.RBAC_A.A_VIEW
        )
      ],
      true
    );

    if (!hasSitesView) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to access sites.'
      );
      this.router.navigate(['/sites']);
      return;
    }

    // Check if user has ANY permission for site documents (view OR create OR edit OR delete)
    const hasAnyDocumentPermission = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES_DOCUMENTS,
          RBACService.RBAC_A.A_VIEW
        ),
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES_DOCUMENTS,
          RBACService.RBAC_A.A_CREATE
        )
      ],
      false
    ); // false = OR logic - user needs ANY of these permissions

    if (!hasAnyDocumentPermission) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to access site documents.'
      );
      this.router.navigate(['/sites']);
      return;
    }

    // Build displayed columns - only include select column if user has delete permission
    this.displayedColumns = [
      ...(this.canDelete ? ['select'] : []),
      'id',
      'name',
      'media',
      'links',
      'sites',
      'expiry',
      'created_at',
      'buttons'
    ];

    // Get the site id from the route params.
    this.parent_id = Number(this.route.parent.snapshot.params['parent_id']);
    this.child_id = Number(this.route.parent.snapshot.params['child_id']);

    // Check if we are updating a site or creating a new one.
    if (
      this.child_id ||
      (typeof this.route.parent.snapshot.params['child_id'] == 'undefined' &&
        this.parent_id)
    ) {
      // Store the site id.
      this.site_id = this.child_id ? this.child_id : this.parent_id;

      this.dataSource.siteId = this.site_id;
      this.dataSource.site_ids.push(this.site_id);

      // Only load data if user has view permission
      const hasViewPermission = this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES_DOCUMENTS,
            RBACService.RBAC_A.A_VIEW
          )
        ],
        true
      );

      if (hasViewPermission) {
        this.dataSource.getData(false);
      }
    }
  }

  async onDelete(folder: FolderModel) {
    this.utils.showModal(
      'Remove Folder',
      `Are you sure you want to remove the "${folder.name}" folder?`,
      () => {
        this.folderService.delete([folder.id]).subscribe(() => {
          this.utils.showToast(`The folder was deleted.`);
          this.dataSource.getData(true);
        });
      }
    );
  }

  ngAfterViewInit() {
    // The table, its matSort and its mat-paginator all live inside a container gated by
    // *ngIf="canView", so without that permission Angular never creates those view
    // children and this.sort / this.paginator stay undefined. Bail out before wiring them up.
    if (!this.canView) {
      return;
    }

    this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));

    const _tap = tap(() => {
      this.dataSource.limit = this.paginator.pageSize;
      this.dataSource.offset = this.paginator.pageIndex;
      this.dataSource.sort_by = this.sort.active;
      this.dataSource.sort_order = this.sort.direction;

      // Sort UTC dates by its corresponding date column.
      if (this.sort.active == 'created_at_UTC') {
        this.dataSource.sort_by = 'created_at';
      }

      this.dataSource.getData(false);
    });

    this.paginator.page.pipe(_tap).subscribe();
    this.sort.sortChange.pipe(_tap).subscribe();
  }

  onView(folder: FolderModel) {
    this.dialog
      .open(TemplateDocsViewComponent, {
        width: '750px',
        data: {
          folder: folder,
          is_training_doc: false,
          permissions: {
            feature: RBACService.RBAC_F.F_SITES_DOCUMENTS,
            canCreate: false,
            canEdit: false,
            canDelete: false
          }
        }
      })
      .afterClosed()
      .subscribe((result) => {
        // Refresh the list regardless of how the dialog is closed.
        // this.dataSource.getData(true);
      });
  }

  onAdd() {
    // Check permissions for creating site documents
    if (
      !this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES,
            RBACService.RBAC_A.A_VIEW
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES_DOCUMENTS,
            RBACService.RBAC_A.A_CREATE
          )
        ],
        true
      )
    ) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to create site documents.'
      );
      return;
    }

    const folder = {} as FolderModel;
    folder.sites = [{ id: this.dataSource.siteId } as SiteModel];
    folder.site_specific = true;
    this.openDialog(folder);
  }

  onEdit(folder: FolderModel) {
    // Check permissions for editing site documents
    if (
      !this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES,
            RBACService.RBAC_A.A_VIEW
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES_DOCUMENTS,
            RBACService.RBAC_A.A_VIEW
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES_DOCUMENTS,
            RBACService.RBAC_A.A_EDIT
          )
        ],
        true
      )
    ) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to edit site documents.'
      );
      return;
    }

    this.openDialog(folder);
  }

  private openDialog(folder: FolderModel) {
    this.dialog
      .open(AccountDocsLayoutComponent, {
        width: '1200px',
        data: {
          folder: folder,
          is_training_doc: false,
          show_document_types_list_for: 'account',
          show_document_visibility: false,
          permissions: {
            feature: RBACService.RBAC_F.F_SITES_DOCUMENTS,
            canCreate: this.rbac.userCanAccess(
              [
                RBACService.getPermission(
                  RBACService.RBAC_F.F_SITES,
                  RBACService.RBAC_A.A_VIEW
                ),
                RBACService.getPermission(
                  RBACService.RBAC_F.F_SITES_DOCUMENTS,
                  RBACService.RBAC_A.A_CREATE
                )
              ],
              true
            ),
            canEdit: this.rbac.userCanAccess(
              [
                RBACService.getPermission(
                  RBACService.RBAC_F.F_SITES,
                  RBACService.RBAC_A.A_VIEW
                ),
                RBACService.getPermission(
                  RBACService.RBAC_F.F_SITES_DOCUMENTS,
                  RBACService.RBAC_A.A_VIEW
                ),
                RBACService.getPermission(
                  RBACService.RBAC_F.F_SITES_DOCUMENTS,
                  RBACService.RBAC_A.A_EDIT
                )
              ],
              true
            ),
            canDelete: this.rbac.userCanAccess(
              [
                RBACService.getPermission(
                  RBACService.RBAC_F.F_SITES,
                  RBACService.RBAC_A.A_VIEW
                ),
                RBACService.getPermission(
                  RBACService.RBAC_F.F_SITES_DOCUMENTS,
                  RBACService.RBAC_A.A_VIEW
                ),
                RBACService.getPermission(
                  RBACService.RBAC_F.F_SITES_DOCUMENTS,
                  RBACService.RBAC_A.A_DELETE
                )
              ],
              true
            )
          }
        }
      })
      .afterClosed()
      .subscribe((result) => {
        // Refresh the list regardless of how the dialog is closed.
        this.dataSource.getData(true);
      });
  }

  onRemoveSelected() {
    this.utils.showModal(
      'Remove Folders',
      `Are you sure you want to remove ${
        this.dataSource.selection.selected.length
      } Folders?`,
      () => {
        this.remove(this.dataSource.selection.selected);
      }
    );
  }

  private remove(ids: number[]) {
    // Check permissions for deleting site documents
    if (
      !this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES,
            RBACService.RBAC_A.A_VIEW
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES_DOCUMENTS,
            RBACService.RBAC_A.A_VIEW
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES_DOCUMENTS,
            RBACService.RBAC_A.A_DELETE
          )
        ],
        true
      )
    ) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to delete site documents.'
      );
      return;
    }

    this.folderService.delete(ids).subscribe(() => {
      this.utils.showToast('Removed Folder(s)');
      this.dataSource.selection.clear();
      this.dataSource.getData(true);
    });
  }

  onFilter() {
    this.utils.showComponentDialog(
      SitesDocsFilterComponent,
      {
        selected_site_ids: this.dataSource.site_ids,
        parent_site_id: this.parent_id
      },
      {
        width: '350px'
      },
      (results) => {
        if (typeof results !== 'undefined') {
          this.dataSource.site_ids = results.site_ids;

          if (this.dataSource.site_ids.length === 0) {
            this.dataSource.site_ids.push(this.site_id);
          }

          this.dataSource.getData();
        }
      }
    );
  }

  /**
   * Format the text display for associated sites.
   * Examples:
   *  - Head Office
   *  - 0 Sites
   *  - 1 Site (If name is not accessible otherwise the name of the site)
   *  - 2 Sites
   *  - Etc.
   * @param form
   */
  formatSitesListColumnText(document: FolderModel) {
    // Check if we have one user.
    if (document.sites_count == 1) {
      // Check if we have details of one user.
      if (document.sites[0]) {
        return document.sites[0].name;
      }
      // Respond with a generic 1 User response.
      return (
        document.sites_count +
        ' ' +
        this.utils.getLangTerm('parent-child-sites-combined.singular', 'Site')
      );
    }
    // Respond with the users count.
    return (
      document.sites_count +
      ' ' +
      this.utils.getLangTerm('parent-child-sites-combined.plural', 'Sites')
    );
  }

  /**
   * The tooltip text is only used if there is more than 1 site.
   * @param form
   */
  formatSitesListColumnTooltip(document: FolderModel) {
    if (document.sites_count > 1) {
      const site_names: string[] = document.sites.map((site: SiteModel) => {
        return site.name;
      });
      return site_names.join(`\n`);
    }
    return '';
  }

  // Expose RBACService to template
  protected readonly RBACService = RBACService;
}

class SitesLibraryDataSource extends CustomDataSource {
  sort_by = 'name';
  sort_order = 'asc';

  siteId: number;

  site_ids: number[] = [];

  getData(resetOffset = false) {
    this.getDataFromLaravelAPI(
      `folders`,
      resetOffset,
      {},
      {
        site_id: this.siteId,
        eager_load: true,
        site_ids: this.site_ids.join(',')
      }
    );
  }
}
